import { constants as config, image as imageConfig } from '@config';
import { setDataBounding } from 'src/utils/sanitize/dimensions-atributes';

const { isDataURL, getMIMEType, getMimeTypeFromUrl } = require('../../helpers/dataUrl');
const md5 = require('../utils/md5');

function ImagesFactory () {
  this.imagesData = {};
  this.inlinedImages = {};
  this.base64Images = {};
  this.mimeTypes = {};
  this.imagesBlacklist = [];
}

ImagesFactory.prototype = {
  convertFormats: ['/thumb/', '/mobile/', '/tab/', '/web/', '/largeweb/', '/original/'],
  defaultAmazonDataNames: {
    name: 'name',
    thumb: 'thumb',
    mobile: 'mobile',
    tab: 'tab',
    web: 'web',
    largeweb: 'largeweb',
    original: 'original'
  },

  defaultAmazonVersionDataNames: {
    name: 'name',
    thumb: 'version.thumb',
    mobile: 'version.mobile',
    tab: 'version.tab',
    web: 'version.web',
    largeweb: 'version.largeweb',
    original: 'version.original'
  },

  defaultAPIDataNames: {
    name: 'name',
    thumb: 'src',
    mobile: 'src_mobile',
    tab: 'src_tab',
    web: 'src_web',
    largeweb: 'src_big',
    original: 'src_big'
  },

  getFacebookPhotos: function (params, _callback) {
    this.sendCommand('load/photos/facebook', params, _.bind(function (result) {
      result = $.parseJSON(result);

      if (INKIVE.Helpers.isResponseExists(result)) {
        const newImageData = this.setFacebookImagesData(result);
        _callback(true, newImageData, result);
      } else {
        _callback(false);
      }
    }, this));
  },

  setFacebookImagesData: function (albums) {
    const newData = {};

    _.each(albums, function (album) {
      _.extend(newData, this.setImagesData(album.data, 'fb'));
    }, this);

    return newData;
  },

  getDropboxPhotos: function (params, callback) {
    this.fetchGenericData('load/photos/dropbox', params, callback, 'db');
  },

  getGooglePhotos: function (params, callback) {
    this.fetchGenericData('load/photos/google', params, callback, 'go');
  },

  getFlickerPhotos: function (params, callback) {
    this.fetchGenericData('load/photos/flickr', params, callback, 'fl');
  },

  getInstagramPhotos: function (params, callback) {
    this.fetchGenericData('load/photos/instagram', params, callback, 'in');
  },

  getAssetbankPhotos: function (params, callback) {
    this.fetchGenericData('load/photos/assetbank', params, callback, 'ab');
  },

  getExternalPhotoById: function (params, callback) {
    this.sendCommand('load/photos/getexternalbyid', params, (response) => {
      callback((response && response.trim()) || '');
    });
  },

  fetchGenericData: function (url, params, _callback, loadType) {
    this.sendCommand(url, params, _.bind(function (result) {
      result = $.parseJSON(result);

      if (INKIVE.Helpers.isResponseExists(result)) {
        const newImageData = this.setImagesData(result, loadType);
        _callback(true, newImageData, result);
      } else {
        _callback(false);
      }
    }, this));
  },

  getOriginalUrl: function (url) {
    url = INKIVE.Helpers.getCORSSecureUrl(url);

    if (!url) return;
    if (this.imagesData[url]) return url;

    const convertFormats = this.convertFormats;
    const imageData = _.find(this.imagesData, function (imageData) {
      return _.contains(imageData.version, url);
    });

    return imageData
      ? processImageData.call(this)
      : getGeneratedOriginalUrl.call(this);

    function getGeneratedOriginalUrl () {
      if (this.isAmazonImage(url)) {
        _.each(convertFormats, function (format) {
          url = url.replace(format, '/original/');
        });

        return url;
      }

      return null;
    }

    function processImageData () {
      const url = imageData.version.original;
      const isExternal = this.isExternalImage(url);
      isExternal && INKIVE.DesignFacade.canvas.call('advice.show', 'low-resolution');

      return isExternal
        ? imageData.version.thumb || url
        : url;
    }
  },

  isExternalImage: function (url) {
    if (!url) return false;

    const isWiki = url.match(/^((http|https):\/\/upload.wikimedia.org)/) !== null;
    const isPxfuel = url.match(/(.pxfuel.com)/) !== null;

    return isWiki || isPxfuel;
  },

  isAmazonImage: function (url) {
    if (!url) return false;

    return url.match(/^((http|https):\/\/s3.amazonaws.com)/) !== null ||
      url.indexOf('static.inkive.me') > -1 ||
      url.indexOf('static.inkive.com') > -1 ||
      url.indexOf('static.webrand.com') > -1 ||
      url.indexOf('static.desygner.com') > -1 ||
      url.indexOf('static.photodexia.com') > -1 ||
      url.indexOf('static.makewebrand.xyz') > -1 ||
      url.indexOf('static.makedesygner.xyz') > -1 ||
      url.indexOf('static.buildwebrand.xyz') > -1 ||
      url.indexOf('static.builddesygner.xyz') > -1 ||
      url.indexOf('static.qawebrand.xyz') > -1 ||
      url.indexOf('static.qadesygner.xyz') > -1;
  },

  blacklistImage: function (image) {
    this.imagesBlacklist.push(this.getImageHash(image));
  },

  isBlacklisted: function (image, url) {
    const imageHash = this.getImageHash(image, url);
    return _.any(this.imagesBlacklist, function (hash) {
      return hash === imageHash;
    });
  },

  setImageUrl: function (image, url, originalHash) {
    const hash = this.getImageHash(image, null, originalHash);
    if (url) {
      this.inlinedImages[hash] = url;
    }

    if (!url) {
      url = image.getAttribute('xlink:href');

      if (isDataURL(url)) {
        this.base64Images[hash] = url;
      } else {
        INKIVE.C.IS_MOBILE_APP || this.fetchMimeType(url);
      }
    } else {
      INKIVE.C.IS_MOBILE_APP || this.fetchMimeType(url);
    }
  },

  setBase64: function (hash, url) {
    this.base64Images[hash] = url;
  },

  getBase64: function (hash) {
    return this.base64Images[hash];
  },

  getImageUrl: function (image, url) {
    const hash = this.getImageHash(image, url);

    if (typeof this.inlinedImages[hash] !== 'undefined') {
      return this.inlinedImages[hash];
    }

    const canBeAnUploadedCopy = isDataURL(url || image.getAttribute('xlink:href'));
    if (canBeAnUploadedCopy) {
      const dataHash = md5(this.getHashableSubstring(url || image.getAttribute('xlink:href')));
      const sameSourceOnOtherImage = _.find(this.inlinedImages, function (value, hash) {
        const hashParts = hash.split('_');

        return hashParts[hashParts.length - 1] === dataHash;
      });

      if (typeof sameSourceOnOtherImage !== 'undefined') {
        return sameSourceOnOtherImage;
      }
    }

    return $(image).data('url');
  },

  getImageHash: function (imageOrId, url, originalHash) {
    let imageElement = null;

    if (typeof imageOrId === 'string') {
      const id = imageOrId;
      imageElement = document.querySelector(`image#${id}`);
      // If image element doesn't exist, make an ID and URL based hash
      if (originalHash) {
        return buildImageHash(id, originalHash);
      }
      if (!imageElement) {
        return buildImageHash(id, md5(this.getHashableSubstring(url || null)));
      }
    }

    // If image element exists or it is given as a parameter
    const image = imageElement || imageOrId;

    const id = image.getAttribute('id');
    const existingHash = image.getAttribute('data-hash');
    if (existingHash) return buildImageHash(id, existingHash);

    const inlinedData = image.getAttribute('xlink:href') || image.dataset.url;

    return buildImageHash(id, md5(this.getHashableSubstring(inlinedData)));

    function buildImageHash (id, hash) {
      return `${id}_${hash}`;
    }
  },

  getImageDataHash: function (imageOrId) {
    if (!imageOrId) return undefined;

    if (typeof imageOrId === 'string') return imageOrId;

    const inlinedData = imageOrId.getAttribute('xlink:href') || imageOrId.dataset.url;
    return md5(this.getHashableSubstring(inlinedData));
  },

  getHashableSubstring: function (url) {
    if (!url) return url;
    const maxLen = 100;

    if (url.length < maxLen || url.substring(0, 4) === 'http') {
      return url;
    }

    return (url.length + url).substr(0, maxLen);
  },

  getAllImageVersions: function (url) {
    const allVersions = {};

    _.each(this.defaultAmazonDataNames, _.bind(function (version) {
      if (version !== 'name') {
        allVersions[version] = this.getVersionUrl(url, version);
      }
    }, this));

    return allVersions;
  },

  getVersionUrl: function (url, size, skipImageData) {
    if (!url) return;

    const imageData = _.find(this.imagesData, function (imageData) {
      return _.contains(imageData.version, url);
    });

    const isImageSvg = url.includes('.svg');
    if (isImageSvg) {
      return INKIVE.Helpers.getCORSSecureUrl(url);
    }

    if (imageData && (imageData.version[size] || !skipImageData)) {
      return INKIVE.Helpers.getCORSSecureUrl(imageData.version[size]);
    }

    if (this.isAmazonImage(url) && size) {
      let keywordPosition;
      let replacement = null;

      _.each(this.convertFormats, function (format) {
        const position = url.indexOf(format);

        if (position !== -1 && (!replacement || position < keywordPosition)) {
          replacement = format;
        }
      });

      if (replacement) {
        url = url.replace(replacement, '/' + size + '/');
      } else if (url.indexOf('/img/backgrounds/') > -1) {
        _.each(this.convertFormats, function (format) {
          format = '-' + format.replace(/\//g, '') + '.';
          const position = url.indexOf(format);

          if (position !== -1 && (!replacement || position < keywordPosition)) {
            replacement = format;
          }
        });
        url = url.replace(replacement, '-' + size + '.');
      }
    }

    const replacedUrl = url.replace('/original/', '/' + size + '/');

    return INKIVE.Helpers.getCORSSecureUrl(replacedUrl);
  },

  getRightSizeUrl: function (url, imageSize, skipImageData, options = { isBackgroundRemoved: false }) {
    const sizesData = this.getAvailableImageSizes(url);
    const { isBackgroundRemoved } = options;

    const width = imageSize.width;
    const height = imageSize.height;
    const rightSize = _.find(sizesData, function (sizeData) {
      return width <= sizeData.width && height <= sizeData.height;
    });

    return rightSize && !isBackgroundRemoved
      ? this.getVersionUrl(url, rightSize.name, skipImageData)
      : INKIVE.Helpers.getCORSSecureUrl(url);
  },

  getRightSizeUrls: function (url, imageSize, skipImageData) {
    const zoom = INKIVE.Tool.getInstance().editor.canvas.getZoom() || 1;
    const width = imageSize.width * zoom;
    const height = imageSize.height * zoom;
    const isRemovedBgrImage = url.endsWith('_bgr');

    /**
     * Try to get the biggest sizes matching the image, then fallback to smaller
     * image sizes if not found. If no other option, then fallback to original size.
     */
    const availableSizes = this.getAvailableImageSizes(url);
    const sizesToGet = availableSizes
      .filter(sizeData => sizeData.name !== 'original' && // Original is added later as special fallback case (due to order)
        (
          sizeData.name === 'thumb' || // We always add the thumbnail (just in case the size is really small)
          width >= sizeData.width || height >= sizeData.height
        )
      )
      .sort((a, b) => a.width > b.width ? -1 : 1)
      .map(sizeData => sizeData.name);

    const nextSmallerSizeIndex = availableSizes.findIndex(s => s.name === sizesToGet[0]);
    const bestFit = availableSizes[nextSmallerSizeIndex + 1].name;
    bestFit === 'original' || sizesToGet.unshift(bestFit);
    sizesToGet.push('original');

    return (sizesToGet.length && !isRemovedBgrImage)
      ? this.getVersionUrls(url, sizesToGet, skipImageData)
      : [INKIVE.Helpers.getCORSSecureUrl(url)];
  },

  getVersionUrls: function (url, sizeNames, skipImageData) {
    return sizeNames.map(sizeName => this.getVersionUrl(url, sizeName, skipImageData));
  },

  getAvailableImageSizes: function (url) {
    const defaultSizes = Object.entries(imageConfig.sizes)
      .map(([name, { width, height }]) => ({ name, width, height }));

    const originalUrl = this.getOriginalUrl(url) || url;
    const imageData = this.getImageOriginalData(originalUrl);
    let attributesNames;

    if (imageData) {
      attributesNames = this.imagesData[originalUrl].attributesNames;
      const customSizes = _.compact(_.map(
        ['thumb', 'mobile', 'tab', 'web', 'largeweb'],
        getSizeInfoObject, this
      ));

      if (customSizes.length) {
        customSizes.push({
          name: 'original',
          width: Infinity,
          height: Infinity
        });

        return customSizes;
      }
    }

    return defaultSizes;

    function getSizeInfoObject (name) {
      const height = this.getAttribute(imageData, attributesNames[name] + '_height');
      const width = this.getAttribute(imageData, attributesNames[name] + '_width');

      if (height && width) {
        return {
          name,
          width: parseInt(width),
          height: parseInt(height)
        };
      }
    }
  },

  getImageDataFromData: function (imageData, attributesNames) {
    const originalUrl = INKIVE.Helpers.getCORSSecureUrl(this.getAttribute(imageData, attributesNames.original));
    const oldData = this.imagesData[originalUrl] || {};
    const versions = this.CORSSecureClean({
      web: this.getAttribute(imageData, attributesNames.web),
      tab: this.getAttribute(imageData, attributesNames.tab),
      thumb: this.getAttribute(imageData, attributesNames.thumb),
      mobile: this.getAttribute(imageData, attributesNames.mobile),
      largeweb: this.getAttribute(imageData, attributesNames.largeweb),
      original: originalUrl
    });

    return {
      name: this.getAttribute(imageData, attributesNames.name),
      stored: oldData.stored || false,
      originalData: imageData,
      attributesNames,
      version: versions
    };
  },

  getAttribute: function (imageData, name) {
    if (!imageData || !name) return;
    const nameChain = name.split('.');
    let tmpObj = imageData;

    while (tmpObj && nameChain[0]) {
      tmpObj = tmpObj[nameChain.shift()];
    }

    return tmpObj;
  },

  getImageOriginalData: function (url) {
    const originalSrc = this.getOriginalUrl(url);
    const imageData = this.imagesData[originalSrc];

    return imageData && imageData.originalData;
  },

  getImageOriginalSizeData: function (url, _callback) {
    const originalSrc = this.getOriginalUrl(url);
    const imageData = this.imagesData[originalSrc];

    if (imageData) {
      const originalData = imageData.originalData;
      if (originalData.original_width && originalData.original_height) {
        _callback({
          width: parseFloat(originalData.original_width),
          height: parseFloat(originalData.original_height)
        });

        return;
      }
    }

    let hasSize;
    const img = new Image();
    const supportsNatural = 'naturalWidth' in img;
    const interval = setInterval(checkSize, 100);

    img.onload = onHasSize;
    // img.crossOrigin = 'Anonymous';
    img.src = INKIVE.Helpers.getCORSSecureUrl(originalSrc);

    function onHasSize () {
      if (hasSize) return;
      clearInterval(interval);
      hasSize = true;

      img.onload = img.onerror = $.noop;
      _callback({
        width: supportsNatural ? img.naturalWidth : img.width,
        height: supportsNatural ? img.naturalHeight : img.height
      });

      img.src = '';
    }

    function checkSize () {
      const hasSize = img.naturalWidth || img.width > 50;
      hasSize && onHasSize();
    }
  },

  storeImageData: function (imageUrl) {
    const originalSrc = this.getOriginalUrl(imageUrl);
    const imageData = this.imagesData[originalSrc];

    if (!imageData || imageData.stored) {
      return;
    }

    const params = {
      scrapbookId: INKIVE.C.BOOK.id,
      images: JSON.stringify(imageData.originalData),
      loadType: imageData.loadType
    };

    this.sendCommand('inkiveeditor/storeImageData', params, function () {
      imageData.stored = true;
    });
  },

  setImagesData: function (imagesData, loadType, attributesNames) {
    const newData = {};
    attributesNames = attributesNames || (loadType
      ? this.defaultAPIDataNames
      : this.defaultAmazonDataNames);

    _.each(imagesData, function (imageData) {
      const newImageData = this.getImageDataFromData(imageData, attributesNames);
      if (loadType) newImageData.loadType = loadType;
      const originalSrc = newImageData.version.original;
      newData[originalSrc] = this.imagesData[originalSrc] = newImageData;

      this.CORSSecureClean(this.imagesData[originalSrc].versions);
      INKIVE.C.IS_MOBILE_APP || this.fetchMimeType(originalSrc);
    }, this);

    return newData;
  },

  // We need to redesign (encapsulate) every outside call to this function
  addImageData: function (imageData, dataNames) {
    dataNames = dataNames || this.defaultAmazonVersionDataNames;
    const newImageData = this.getImageDataFromData(imageData, dataNames);
    const originalSrc = newImageData.version.original;
    this.imagesData[originalSrc] = newImageData;

    this.CORSSecureClean(this.imagesData[originalSrc].versions);
    INKIVE.C.IS_MOBILE_APP || this.fetchMimeType(originalSrc);
  },

  CORSSecureClean: function (versions) {
    if (!versions) return versions;

    versions.web = INKIVE.Helpers.getCORSSecureUrl(versions.web);
    versions.tab = INKIVE.Helpers.getCORSSecureUrl(versions.tab);
    versions.thumb = INKIVE.Helpers.getCORSSecureUrl(versions.thumb);
    versions.mobile = INKIVE.Helpers.getCORSSecureUrl(versions.mobile);
    versions.largeweb = INKIVE.Helpers.getCORSSecureUrl(versions.largeweb);
    versions.original = INKIVE.Helpers.getCORSSecureUrl(versions.original);

    return versions;
  },

  smartLoad: function (url, containerSize, callback, isExternal, options = {}) {
    const {
      keepUrl = false,
      isBackgroundRemoved = false,
      s3Source
    } = options;
    const totalAttempts = s3Source ? 4 : 3;
    const newAttemptDelay = isExternal ? 2500 : 1000;

    let attempt = 0;
    const img = new Image();
    const optimumImageToLoad = this.getRightSizeUrl(url, containerSize, null, { isBackgroundRemoved });

    img.onerror = _.bind(onImageLoadError, this);
    img.onload = _.partial(callback, INKIVE.C.Status.success, img);
    img.src = keepUrl || !optimumImageToLoad ? url : INKIVE.Helpers.getCORSSecureUrl(optimumImageToLoad);

    if (!containerSize.skipOverride) {
      img.width = containerSize.width;
      img.height = containerSize.height;
    }

    function onImageLoadError () {
      attempt++;

      if (attempt <= totalAttempts) {
        _.delay(_.bind(function () {
          if (attempt === totalAttempts && s3Source) {
            img.src = s3Source;
          } else {
            img.src = keepUrl
              ? url
              : INKIVE.Helpers.getCORSSecureUrl(attempt < totalAttempts - 1
                ? optimumImageToLoad
                : this.getOriginalUrl(url));
          }
        }, this), newAttemptDelay);
      } else {
        callback(INKIVE.C.Status.timeout, img);
      }
    }
  },

  sendCommand: function (url, data, callback) {
    console.warn('Images Factory: Called old sendCmd: ' + url);

    $.ajax({
      url: INKIVE.C.BASE_URL + url,
      type: 'POST',
      data,
      dataType: 'json',
      complete: function (r) {
        callback(r.responseText);
      }
    });
  },

  getMimeType (imageUrl) {
    const url = this.getOriginalUrl(imageUrl) || imageUrl;

    return this.mimeTypes[url] || getMimeTypeFromUrl(url);
  },

  async fetchMimeType (url) {
    const MIMEType = await getMIMEType(url);
    this.mimeTypes[this.getOriginalUrl(url) || url] = MIMEType;
  },

  removeImageFilter: function (image) {
    const $target = $(image);
    const mimeType = INKIVE.ImagesFactory.getMimeType($target.attr('xlink:href'));
    const $button = $('[data-action=on_imageFilter]');

    $button.removeClass('applied');
    $button.parent().toggle((mimeType === 'image/jpeg' && $.browser.name !== 'msie'));

    if ($target.is('[filter]')) {
      $target.removeAttr('filter');
      $('#svgcontent defs').find('#filter_' + $target.attr('id')).remove();
    }
  },

  addBBoxValues: function (element) {
    const vectorTypes = ['icon', 'logo', 'sticker', 'shape', 'image'];

    const setAttributes = (element) => {
      const e = element.id && INKIVE.DesignFacade.get(element.id);
      if (e && vectorTypes.includes(e.type)) {
        setDataBounding(element, element.getBBox());
      }
    };

    if (element) {
      setAttributes(element);
    } else {
      [config.smartAssets.attributes.userInput, config.smartAssets.attributes.definedAsset].forEach(attr => {
        document.querySelectorAll(`#svgcontent [${attr}]`).forEach((element) => {
          setAttributes(element);
        });
      });
    }
  }
};

module.exports = new ImagesFactory();
